import React from "react";
import { Tooltip } from "@mui/material";
import { Button, Icon, SideTag } from "@/components/ui";
import { PendingOrder, PrevInstruction, ConfirmDialogState, BulkEditFields, BulkCreateFields } from "../types";
import { getIrClass, LHF_CODES, buildModelIrTooltip, buildHoldingStatsTooltip, cashTintClass, fmtNum, isFieldDirty, formatPrice, formatPrevInstruction, formatPrevInstructionCell, formatPrevExecution, formatSessionDate, DEFAULT_PRICE_FMT, type PriceFmt } from "../utils";
import { getTargetCellClass, getModelCellClass } from "../decisionColoring";
import { getTargetRiskStatus, isTargetOverLimit, RiskWarningIcon, type RiskByCompanyId } from "../riskViolations";
import { formatCurrencyInCrores } from "@/utils/formatters";
import { isGlobalCountryCode } from "@/utils/country";
import {
  findMatchedRec,
  isHandedOnCall,
  findLatestModel,
  modelTargetAtHorizon,
  computeImpliedReturn,
  yearsToTarget,
  priceForIr,
  type RawRecommendation,
  type SuggestionMap,
} from "../recommendationSelectors";
import { StrategyBadge } from "./StrategyBadge";
import { ExecutionCell } from "./ExecutionCell";
import { LastOrderDetailsPopover } from "./LastOrderDetailsPopover";
import { CioNotePopover } from "./CioNotePopover";
import styles from "../OrderRecommendationList.module.css";
import ScopeGate from "@/components/utils/ScopeGate";
import { WRITE_ORDERS_AND_RECOMMENDATIONS } from "@/constants/scopeCodes";
import { formatNumber } from "@/utils/formatters";

/** Empty values recede so the real numbers stand out. */
const DASH = <span className={styles.dim}>—</span>;

const DECISION_DISPLAY: Record<string, { variant: string; label: string }> = {
  continue:  { variant: "decision-continue",  label: "Continue"  },
  completed: { variant: "decision-completed",  label: "Completed" },
};

// ── Tooltip helpers (shared by the analyst / IC / model cells) ─────────────

// The Global (SGS) strategy suggestion is shown only on Global rows; Domestic rows
// show every strategy suggestion except SGS. This scoping drives both the hover text
// and the buy/sell cell coloring, so Domestic cells never light up off an SGS
// suggestion and Global cells key only off SGS.
const SGS_STRATEGY_CODE = "SGS";
const keepSuggestion = (code: string, isGlobal: boolean): boolean =>
  isGlobal ? code === SGS_STRATEGY_CODE : code !== SGS_STRATEGY_CODE;

/** Filter a suggestion map to the strategies relevant for the row's tab (SGS on Global,
 *  everything-but-SGS on Domestic) before deriving cell colors. */
const scopeSuggestions = (
  suggestions: SuggestionMap | null | undefined,
  isGlobal: boolean,
): SuggestionMap =>
  Object.fromEntries(
    Object.entries(suggestions ?? {}).filter(([code]) => keepSuggestion(code, isGlobal)),
  );

/** "Strategy Suggestion:" lines — entries with a trade_type, scoped to the row's tab. */
const renderSuggestionLines = (
  suggestions: SuggestionMap | null | undefined,
  isGlobal: boolean,
): React.ReactNode => {
  const entries = Object.entries(suggestions ?? {}).filter(
    ([code, s]) => s?.trade_type != null && keepSuggestion(code, isGlobal),
  );
  if (entries.length === 0) return null;
  return (
    <>
      <span style={{ fontWeight: 600 }}>Strategy Suggestion:</span><br />
      {entries.map(([code, s]) => (
        <React.Fragment key={code}>&nbsp;&nbsp;{code}: {s.trade_type}<br /></React.Fragment>
      ))}
    </>
  );
};

/** The marker beside an Analyst / IC value filed by someone who has since handed the
 *  company on. The call is still the latest one in that role. */
const PrevOwnerIcon: React.FC<{ label: string }> = ({ label }) => (
  <Tooltip placement="top" title={`Previous ${label} — no longer holds this company`}>
    <span style={{ color: "var(--text-faint)", fontWeight: 700, marginLeft: 3, cursor: "help" }}>*</span>
  </Tooltip>
);

/** A value with its markers (⚠, *, ⓘ) after it: the value is centred in the cell, and moves
 *  left only as far as the markers need to stay inside it. */
const ValueWithMarkers: React.FC<{ markers?: React.ReactNode; children: React.ReactNode }> = ({ markers, children }) => (
  <span className={styles["value-line"]}>
    <span className={styles["value-main"]}>{children}</span>
    <span className={styles["marker-slot"]}>{markers}</span>
  </span>
);

/** The tag shown after the author's name once they have handed the company on. */
const PREV_OWNER_TAG = " (Prev.)";

/** Target % tooltip for an analyst / IC recommendation. `fmtRec` formats the rec price
 *  ("₹1,234.50" domestic, "1,234.50 (USD)" global). */
const buildTargetTooltip = (
  rec: RawRecommendation | null,
  nameDisplay: string | null,
  fmtRec: PriceFmt = DEFAULT_PRICE_FMT,
  isGlobal = false,
  isPrev = false,
): React.ReactNode =>
  rec ? (
    <span>
      {nameDisplay && <>{nameDisplay}{isPrev && PREV_OWNER_TAG}<br /></>}
      {rec.decision && <>{rec.decision}<br /></>}
      {rec.decision_price != null && <>Rec Price: {fmtRec(rec.decision_price)}<br /></>}
      {renderSuggestionLines(rec.suggestions, isGlobal)}
    </span>
  ) : "";

/** Implied-return tooltip for an analyst / IC recommendation. `fmtRec` formats the
 *  target (rec) price, `fmtCmp` the CMP — "₹" domestic, "(CODE)" suffix global. */
const buildIrTooltip = (
  label: string,
  rec: RawRecommendation | null,
  nameDisplay: string | null,
  targetPrice: number | null,
  ir: number | null,
  latestPrice: number | null | undefined,
  fmtRec: PriceFmt = DEFAULT_PRICE_FMT,
  fmtCmp: PriceFmt = DEFAULT_PRICE_FMT,
  isPrev = false,
): React.ReactNode =>
  ir != null ? (
    <span>
      {nameDisplay && <>{label}: {nameDisplay}{isPrev && PREV_OWNER_TAG}<br /></>}
      {rec?.decision && <>{rec.decision}<br /></>}
      {targetPrice != null && <>Target: {fmtRec(targetPrice)}<br /></>}
      {latestPrice != null && <>CMP: {fmtCmp(latestPrice)}</>}
    </span>
  ) : "";


/** Hover detail for the Prev. Instr. cell: one block per past session, newest first. */
const buildPrevInstructionsTooltip = (
  entries: PrevInstruction[],
  fmtPrice: PriceFmt,
): React.ReactNode => {
  if (entries.length === 0) return "";
  return (
    <div style={{ minWidth: 190 }}>
      {entries.map((entry, index) => {
        const detail = formatPrevInstruction(entry);
        const execution = formatPrevExecution(entry, fmtPrice);
        return (
          <div key={entry.session_date} style={{ marginTop: index === 0 ? 0 : 8 }}>
            <span style={{ fontWeight: 600 }}>{formatSessionDate(entry.session_date)}</span>
            {entry.order_type === 2 ? " · SELL" : entry.order_type === 1 ? " · BUY" : ""}
            {entry.is_completion ? " · Completed" : ""}
            <br />
            {detail}
            {execution && <>{detail && <br />}{execution}</>}
            {entry.comment && <><br /><span style={{ opacity: 0.85 }}>{entry.comment}</span></>}
          </div>
        );
      })}
    </div>
  );
};


// ── Props ──────────────────────────────────────────────────────────────────

interface BaseProps {
  order: PendingOrder;
  isFirst: boolean;
  rowSpan: number;
  first: PendingOrder;
  isBulkEditing: boolean;
  /** When true, the row failed pre-save validation and is highlighted. */
  isErrorRow?: boolean;
  lastOrderFilter?: string;
  /** Core / LHF liquidity limits keyed by company id, for flagging over-limit targets. */
  riskByCompanyId: RiskByCompanyId;
  /** Patches the row after a CIO note is saved, so the icon updates without a refetch. */
  applyCioNote: (companyId: number, cioNote: string | null) => void;
}

interface PendingProps extends BaseProps {
  variant: "pending";
  bulkEditState: Record<number, BulkEditFields>;
  /** Field values as they were when edit mode was entered — diffed against the live
   *  draft to highlight which fields the CIO has actually changed this session. */
  bulkEditSnapshot: Record<number, BulkEditFields>;
  handleBulkEditChange: (id: number, field: keyof BulkEditFields, value: BulkEditFields[keyof BulkEditFields]) => void;
  setConfirmDialog: React.Dispatch<React.SetStateAction<ConfirmDialogState>>;
}

interface CreateProps extends BaseProps {
  variant: "indicator" | "rec";
  strategyGroup: "LHF" | "CORE";
  bulkCreateState: Record<string, BulkCreateFields>;
  /** Seeded default values captured the moment a create row was first touched —
   *  diffed against the live draft to highlight which fields have actually been set. */
  bulkCreateSnapshot: Record<string, BulkCreateFields>;
  handleBulkCreateChange: (companyId: number, sg: "LHF" | "CORE", field: keyof BulkCreateFields, value: BulkCreateFields[keyof BulkCreateFields]) => void;
}

type TableRowProps = PendingProps | CreateProps;

// ── Component ─────────────────────────────────────────────────────────────

const TableRowComponent: React.FC<TableRowProps> = (props) => {
  const { order, isFirst, rowSpan, first, isBulkEditing, isErrorRow, riskByCompanyId, applyCioNote } = props;

  // Global (non-Indian) rows suffix prices with the currency code in brackets, e.g.
  // "1,234.50 (USD)": recommendation prices use raw's currency_code, the CMP uses the
  // LTP's ltp_currency_code. Domestic rows keep "₹" in tooltips and no symbol on the
  // visible CMP cell.
  const isGlobal = isGlobalCountryCode(first.country_code);
  const fmtRecPrice: PriceFmt = (v) => formatPrice(v, first.currency_code, isGlobal);
  const fmtCmpTooltip: PriceFmt = (v) => formatPrice(v, first.ltp_currency_code, isGlobal);
  const fmtCmpCell: PriceFmt = (v) => formatPrice(v, first.ltp_currency_code, isGlobal, "");

  // Variant-specific draft state
  const isPending = props.variant === "pending";
  const cp = !isPending ? (props as CreateProps) : null;

  const isLhfRow = isPending
    ? (order.strategy_group === "LHF" || LHF_CODES.has(order.strategy_code || ""))
    : cp!.strategyGroup === "LHF";
  const isBuy = order.order_type === 1;
  const hft = order.holdings_by_firm_type || {};
  const stats = order.holdings_stats_by_firm_type ?? {};

  // Global (non-Indian) rows show a single "SGS" (Global strategy) holding column
  // instead of the PMS/AIF/IFSC (or LHF/LHF_AIF) sub-columns.
  const holdingCol1 = isGlobal ? hft.SGS : isLhfRow ? hft.LHF : hft.PMS;
  const holdingCol2 = isLhfRow ? hft.LHF_AIF : hft.AIF;
  const holdingCol3 = isLhfRow ? null : hft.IFSC_AIF;
  const label1 = isGlobal ? "SGS" : isLhfRow ? "LHF" : "PMS";

  // Cash-availability tint for the primary holding column (PMS / LHF / SGS): orange bucketed
  // by the count of clients that can still buy (below target with enough cash; target = CIO
  // target or, when none, the group's current holding %).
  const pmsStats = stats[label1];
  // "Can buy" is a buy-side concept, so it's suppressed only on pending-order SELL rows.
  // It IS shown on recommendation rows (which carry order_type 0) and on placeholder /
  // indicator rows, since those are buy-oriented. This gates both the holding-cell tint
  // and the tooltip's "Target" + "Buyable clients" block (see showBuyable).
  const showBuyable = !(isPending && !isBuy);
  const pmsCashCount = showBuyable ? (pmsStats?.accounts_below_target_with_cash ?? 0) : 0;
  const pmsCashClass = cashTintClass(pmsCashCount);

  // ── Derive analyst / IC / model values from the raw recommendations array ────
  // Every author's view is read at the row's own horizon — 5yr for LHF, 3yr for CORE —
  // so an LHF row never quotes a three-year target beside a five-year model one.
  const horizonYears = isLhfRow ? 5 : 3;
  const today = new Date();
  const companyRecs = first.recommendations ?? [];
  const analystRec = findMatchedRec(companyRecs, first.latest_rec_analyst_name_oms, horizonYears);
  const icRec      = findMatchedRec(companyRecs, first.latest_rec_ic_user_name_oms, horizonYears);

  const analystTargetPrice = priceForIr(analystRec);
  const icTargetPrice      = priceForIr(icRec);
  const analystIr = computeImpliedReturn(analystTargetPrice, first.latest_price, yearsToTarget(analystRec?.target_date, today));
  const icIr      = computeImpliedReturn(icTargetPrice,      first.latest_price, yearsToTarget(icRec?.target_date,      today));
  const analystNameDisplay = analystRec?.recommended_by || first.analyst_name_oms || null;
  const icNameDisplay      = icRec?.recommended_by      || first.ic_user_name_oms || null;
  // Set when the company has been reassigned since this call was filed. Who holds it now
  // is deliberately not surfaced on this page — only that it changed.
  const analystPrevOwner = isHandedOnCall(analystNameDisplay, first.analyst_name_oms);
  const icPrevOwner      = isHandedOnCall(icNameDisplay,      first.ic_user_name_oms);

  const analystCellClass = getTargetCellClass(analystRec?.decision, scopeSuggestions(analystRec?.suggestions, isGlobal), analystRec?.decision_price, first.latest_price);
  const icCellClass      = getTargetCellClass(icRec?.decision,      scopeSuggestions(icRec?.suggestions, isGlobal),      icRec?.decision_price,      first.latest_price);


  // Liquidity-risk breach checks, against the Core portfolio's limit only. raw writes
  // holding_level onto the three-year row alone, so an LHF row's Analyst / IC target is
  // null and never flags — the five-year view is a price call, not a position size.
  // The CIO target is per-row; LHF rows have no Core-equivalent limit, so never flag.
  const companyRisk = riskByCompanyId[first.company] ?? null;
  const analystTarget = analystRec?.target_weight ?? null;
  const icTarget      = icRec?.target_weight ?? null;
  const analystRiskBreach = getTargetRiskStatus(analystTarget, companyRisk).hasBreach;
  const icRiskBreach      = getTargetRiskStatus(icTarget, companyRisk).hasBreach;
  const cioRiskItem = isLhfRow ? null : companyRisk?.core;
  const cioRiskBreach = isPending && isTargetOverLimit(order.cio_target_pct, cioRiskItem);

  // Model: prefer Target Price rec; fall back to Buy Below for target_price / target_date.
  // Decision is preserved from the Target Price rec when both exist (mt wins).
  const rowRecs = order.recommendations ?? companyRecs;
  const model  = modelTargetAtHorizon(rowRecs, horizonYears);
  const mtRec  = model.targetPriceRec;
  const mbRec  = model.buyBelowRec;
  const ivRec  = !isLhfRow ? findLatestModel(rowRecs, "Model Intrinsic Value", horizonYears) : null;
  const { decision: modelDecision, buyBelow, targetPrice, targetDate } = model;
  const modelIr = computeImpliedReturn(targetPrice, order.latest_price, yearsToTarget(targetDate, today));

  // Suggestion shown alongside the model IR: add-side → Buy Below's suggestions,
  // reduce-side → Target Price's suggestions, anything else → none.
  const modelDecisionLower = (modelDecision ?? "").toLowerCase();
  const modelSuggestion = modelDecisionLower.includes("reduce")
    ? (mtRec?.suggestions ?? null)
    : modelDecisionLower.includes("add")
      ? (mbRec?.suggestions ?? null)
      : null;
  const modelCellClass = getModelCellClass(modelDecision, buyBelow, targetPrice, order.latest_price, [scopeSuggestions(modelSuggestion, isGlobal)]);

  const pendingDraft: Partial<BulkEditFields> = isPending && isBulkEditing
    ? ((props as PendingProps).bulkEditState[order.id] ?? {}) : {};
  const createDraft: Partial<BulkCreateFields> = cp && isBulkEditing
    ? (cp.bulkCreateState[`${order.company}_${cp.strategyGroup}`] ?? {}) : {};

  // "As entered" snapshots to diff drafts against for the dirty-field highlight —
  // saved values for a pending row, seeded defaults for a create row.
  const pendingSnapshot: Partial<BulkEditFields> = isPending && isBulkEditing
    ? ((props as PendingProps).bulkEditSnapshot[order.id] ?? {}) : {};
  const createSnapshot: Partial<BulkCreateFields> = cp && isBulkEditing
    ? (cp.bulkCreateSnapshot[`${order.company}_${cp.strategyGroup}`] ?? {}) : {};
  const isDirty = (field: keyof BulkCreateFields): boolean =>
    isPending
      ? isFieldDirty(pendingDraft, pendingSnapshot, field)
      : isFieldDirty(createDraft, createSnapshot, field);

  // Live breach check against the in-progress draft value, so the CIO sees the alert
  // while typing rather than only after saving (cioRiskBreach checks the saved value).
  const pendingDraftBreach = isTargetOverLimit(pendingDraft.cio_target_pct, cioRiskItem);
  const createDraftBreach = isTargetOverLimit(createDraft.cio_target_pct, cioRiskItem);

  // Create rows stay read-only until a Buy/Sell decision is chosen, so a CIO
  // position can never be entered without a direction (and silently dropped).
  const createLocked = !!cp && isBulkEditing && createDraft.order_type == null;

  // The error highlight lives in CSS (.error-row) so it can paint the cell backgrounds.
  const rowClass = [
    styles["order-row"],
    isErrorRow ? styles["error-row"] : "",
  ].filter(Boolean).join(" ");

  const showPrevFlag = props.variant !== "rec" && !!first.has_yesterday_order;
  const showAifFlag = first.aif_lt_qty != null || first.aif_st_qty != null;

  return (
    <tr className={rowClass}>
      {/* Company — spans */}
      {isFirst && (
        <td rowSpan={rowSpan}>
          <span className={styles["company-name"]}>{first.company_name || "N/A"}</span>
          {first.company_category && <span className={styles["company-category"]}>{first.company_category}</span>}
          {first.company != null && (
            <CioNotePopover
              companyId={first.company}
              companyName={first.company_name}
              cioNote={first.cio_note}
              onSaved={applyCioNote}
            />
          )}

          {(showPrevFlag || showAifFlag) && (
            <div className={styles["company-flags"]}>
              {/* Prev tag: Only for non-rec rows */}
              {showPrevFlag && (
                <Tooltip
                  placement="top"
                  title="An order was already placed for this company on the last working day."
                >
                  <span className={styles["company-legend-flag"]}>Prev</span>
                </Tooltip>
              )}

              {/* AIF tag: For ALL rows */}
              {showAifFlag && (
                <Tooltip
                  placement="top"
                  title={
                    <div style={{ whiteSpace: "pre-line", textAlign: "center" }}>
                      {`AIF Split: ${formatNumber(first.aif_lt_qty ?? 0, 0)} LT | ${formatNumber(first.aif_st_qty ?? 0, 0)} ST`}
                      {(first.aif_lt_qty === 0 && first.oldest_aif_st_lot_age_days != null) &&
                        `\nOldest ST Lot: ${first.oldest_aif_st_lot_age_days} days (${first.oldest_aif_st_lot_date})`
                      }
                    </div>
                  }
                >
                  <span
                    className={`${styles["aif-legend-flag"]} ${
                      (first.aif_lt_qty ?? 0) > 0 ? styles["lt-flag"] : styles["st-flag"]
                    }`}
                  >
                    {(first.aif_lt_qty ?? 0) > 0 ? ((first.aif_st_qty ?? 0) > 0 ? "AIF: LT|ST" : "AIF: LT") : "AIF: ST"}
                  </span>
                </Tooltip>
              )}
            </div>
          )}
        </td>
      )}

      {/* Strategy badge + order type */}
      <td className={styles.center}>
        <StrategyBadge
          code={isPending ? order.strategy_code : (cp!.strategyGroup === "LHF" ? "LHF" : "PMS_IEF")}
          group={isPending ? order.strategy_group : cp!.strategyGroup}
        />
        {(() => {
          const orderType = isPending ? order.order_type : createDraft.order_type;
          if (!orderType) return null;
          return (
            <div className={styles["type-side"]}>
              <SideTag side={orderType === 1 ? "buy" : "sell"} colored />
            </div>
          );
        })()}
      </td>

      {/* Execution — type, limit price, method, priority */}
      <ExecutionCell
        order={order}
        isBulkEditing={isBulkEditing}
        isPending={isPending}
        disabled={createLocked}
        draft={isPending ? pendingDraft : createDraft}
        snapshot={isPending ? pendingSnapshot : createSnapshot}
        firstLatestPrice={first.latest_price}
        onFieldChange={(field, value) =>
          isPending
            ? (props as PendingProps).handleBulkEditChange(order.id, field, value)
            : cp!.handleBulkCreateChange(order.company, cp!.strategyGroup, field as keyof BulkCreateFields, value)
        }
      />

      {/* CMP — spans. Global rows suffix the currency code from the LTP source. */}
      {isFirst && (
        <td rowSpan={rowSpan} className={styles.num}>
          {first.latest_price != null ? fmtCmpCell(first.latest_price) : DASH}
        </td>
      )}

      {/* Chg% — spans */}
      {isFirst && (
        <td rowSpan={rowSpan} className={`${styles.num} ${first.today_change ? (first.today_change > 0 ? styles["chg-pos"] : styles["chg-neg"]) : ""}`}>
          {first.today_change != null ? `${first.today_change > 0 ? "+" : ""}${fmtNum(first.today_change, 1)}%` : DASH}
        </td>
      )}

      {/* Days — per-row, not spanned: Core and LHF have different AUM, so each row's
          own days-to-trade figure (computed server-side against that row's own scheme
          AUM) must be shown separately rather than merging on the company-level "first".
          Hidden on Global rows, which drop this column entirely. */}
      {!isGlobal && <td className={styles.num}>{order.days ?? DASH}</td>}

      {/* Decision */}
      {(() => {
        if (isPending) {
          const decision = isBulkEditing ? (pendingDraft.cio_decision ?? order.cio_decision ?? "") : (order.cio_decision ?? "");
          const { variant: decisionVariant = "", label: decisionLabel = "" } = DECISION_DISPLAY[decision] ?? {};
          return (
            <td className={styles.center}>
              {isBulkEditing
                ? <select className={`${styles["decision-select"]} ${decision === "continue" ? styles["val-continue"] : ""} ${isDirty("cio_decision") ? styles["field-dirty"] : ""}`} value={pendingDraft.cio_decision ?? ""} onChange={e => (props as PendingProps).handleBulkEditChange(order.id, "cio_decision", e.target.value || null)}>
                    <option value="">—</option>
                    <option value="continue">Continue</option>
                    <option value="completed">Completed</option>
                  </select>
                : decisionLabel
                  ? <span className={`${styles["decision-badge"]} ${styles[decisionVariant] ?? ""}`}>{decisionLabel}</span>
                  : DASH}
            </td>
          );
        }
        return (
          <td className={styles.center}>
            {isBulkEditing
              ? <select className={`${styles["decision-select"]} ${createDraft.order_type === 1 ? styles["val-continue"] : createDraft.order_type === 2 ? styles["val-stop"] : ""} ${isDirty("order_type") ? styles["field-dirty"] : ""}`} value={createDraft.order_type ?? ""} onChange={e => { const v = e.target.value; cp!.handleBulkCreateChange(order.company, cp!.strategyGroup, "order_type", v === "1" ? 1 : v === "2" ? 2 : null); }}>
                  <option value="">—</option>
                  <option value="1">BUY</option>
                  <option value="2">SELL</option>
                </select>
              : null}
          </td>
        );
      })()}

      {/* Prev. Instr. — the last session's CIO instruction with its date; the two
          sessions before it are on hover. Global rows only, mirroring the domestic-only
          Days column. Not spanned: each tracker carries its own instruction history. */}
      {isGlobal && (() => {
        const prevInstructions = order.prev_instructions ?? [];
        const latest = prevInstructions[0];
        if (!latest) return <td>{DASH}</td>;
        return (
          <td>
            <Tooltip placement="top" title={buildPrevInstructionsTooltip(prevInstructions, fmtCmpTooltip)}>
              <span className={styles["prev-instr"]}>
                <span className={styles["prev-instr-date"]}>{formatSessionDate(latest.session_date)}</span>
                <br />
                {formatPrevInstructionCell(latest, fmtCmpTooltip)}
              </span>
            </Tooltip>
          </td>
        );
      })()}

      {/* Holdings — primary column (PMS/LHF, or SGS on Global rows) with stats tooltip;
          tinted by accounts that can still buy with cash. Global rows show only this one
          SGS column; domestic rows also show the AIF/IFSC (or LHF_AIF) sub-columns. */}
      <td className={`${styles.num} ${pmsCashClass ? styles[pmsCashClass] : ""}`}>
        <Tooltip placement="top" title={buildHoldingStatsTooltip(pmsStats, fmtNum, holdingCol1, showBuyable)}>
          <span>
            {holdingCol1 != null && holdingCol1 > 0 ? `${fmtNum(holdingCol1)}%` : DASH}
          </span>
        </Tooltip>
      </td>
      {!isGlobal && (
        <>
          <td className={styles.num}>{holdingCol2 != null && holdingCol2 > 0 ? `${fmtNum(holdingCol2)}%` : DASH}</td>
          <td className={styles.num}>{holdingCol3 != null && holdingCol3 > 0 ? `${fmtNum(holdingCol3)}%` : DASH}</td>
        </>
      )}

      {/* Last Order — hidden on Global rows, which drop this column entirely. */}
      {!isGlobal && (() => {
        const ordersDict = order.last_orders_by_strategy ?? {};
        const ordersList = Object.values(ordersDict).filter((o): o is NonNullable<typeof o> => o != null);
        const hasAnyOrder = ordersList.length > 0;
        const latestOrder = hasAnyOrder
          ? ordersList.slice().sort((a, b) => (b.order_date ?? "").localeCompare(a.order_date ?? ""))[0]
          : null;

        let displayValue: number | null = null;
        if (latestOrder) {
          if (isBuy) {
            // Buy: order_type 1 = Increase to Target %, 2 = Increase by %.
            if (latestOrder.order_type === 1) displayValue = latestOrder.increase_to_2 ?? null;
            else if (latestOrder.order_type === 2) displayValue = latestOrder.increase_by_2 ?? null;
          } else {
            // Sell: only show condition_2 for Sell-to-Target-% (order_type 1).
            if (latestOrder.order_type === 1) displayValue = latestOrder.condition_2 ?? null;
          }
        }

        const pendingCodes = order.pending_order_codes ?? [];
        const showPendingCodes =
          props.lastOrderFilter === "has_pending_codes" &&
          order.cio_decision === "continue" &&
          pendingCodes.length > 0;
        return (
          <td className={`${styles.num} ${order.last_order_mismatch ? styles["last-order-warning"] : ""}`}>
            <ValueWithMarkers
              markers={hasAnyOrder && (
                <LastOrderDetailsPopover
                  ordersByStrategy={ordersDict}
                  isMismatch={!!order.last_order_mismatch}
                  isBuy={isBuy}
                />
              )}
            >
              {displayValue != null ? `${fmtNum(displayValue)}%` : hasAnyOrder ? null : DASH}
            </ValueWithMarkers>
            {showPendingCodes && (
              <div className={styles["pending-codes"]}>
                Left: {pendingCodes.join(", ")}
              </div>
            )}
          </td>
        );
      })()}

      {/* Analyst target — per-row, not spanned: the rec behind it is read at the row's own
          horizon (5yr on LHF, 3yr on Core), so the two rows quote different views. Spanning
          rendered the cell once from the first sub-row — LHF — and raw writes no
          target_weight on a five-year rec, so the Core row's real three-year target
          disappeared behind the LHF row's blank. Matches Model IR / Last Order / Days. */}
      <td className={`${styles.num} ${styles[analystCellClass] ?? ""}`}>
        <ValueWithMarkers
          markers={<>
            {analystPrevOwner && <PrevOwnerIcon label="Analyst" />}
            {analystRiskBreach && companyRisk && <RiskWarningIcon label="Analyst" targetPct={analystTarget!} risk={companyRisk} />}
          </>}
        >
          <Tooltip placement="top" title={buildTargetTooltip(analystRec, analystNameDisplay, fmtRecPrice, isGlobal, analystPrevOwner)}>
            <span>{analystTarget != null ? `${fmtNum(analystTarget)}%` : DASH}</span>
          </Tooltip>
        </ValueWithMarkers>
      </td>

      {/* IC target — per-row, for the same reason as the Analyst target above. */}
      <td className={`${styles.num} ${styles[icCellClass] ?? ""}`}>
        <ValueWithMarkers
          markers={<>
            {icPrevOwner && <PrevOwnerIcon label="IC" />}
            {icRiskBreach && companyRisk && <RiskWarningIcon label="IC" targetPct={icTarget!} risk={companyRisk} />}
          </>}
        >
          <Tooltip placement="top" title={buildTargetTooltip(icRec, icNameDisplay, fmtRecPrice, isGlobal, icPrevOwner)}>
            <span>{icTarget != null ? `${fmtNum(icTarget)}%` : DASH}</span>
          </Tooltip>
        </ValueWithMarkers>
      </td>

      {/* CIO target % */}
      <td className={styles.num}>
        {isBulkEditing
          ? isPending
            ? <span className={styles["cio-edit"]}>
                <input type="number" step="0.01" placeholder="CIO %" value={pendingDraft.cio_target_pct ?? ""} onChange={e => (props as PendingProps).handleBulkEditChange(order.id, "cio_target_pct", e.target.value)} onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) (props as PendingProps).handleBulkEditChange(order.id, "cio_target_pct", v.toFixed(2)); }} className={`${styles["pot-inline-input"]} ${pendingDraftBreach ? styles["input-breach"] : isDirty("cio_target_pct") ? styles["field-dirty"] : ""}`} />
                {pendingDraftBreach && companyRisk && (
                  <span className={styles["cio-risk-alert"]}>
                    <RiskWarningIcon label="CIO" targetPct={pendingDraft.cio_target_pct!} risk={companyRisk} />
                  </span>
                )}
              </span>
            : <Tooltip placement="top" title={createLocked ? "Select Buy or Sell first" : ""}>
                <span className={styles["cio-edit"]}>
                  <input type="number" step="0.01" placeholder="CIO %" disabled={createLocked} value={createDraft.cio_target_pct ?? ""} onChange={e => cp!.handleBulkCreateChange(order.company, cp!.strategyGroup, "cio_target_pct", e.target.value)} onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) cp!.handleBulkCreateChange(order.company, cp!.strategyGroup, "cio_target_pct", v.toFixed(2)); }} className={`${styles["pot-inline-input"]} ${createLocked ? styles["input-locked"] : createDraftBreach ? styles["input-breach"] : isDirty("cio_target_pct") ? styles["field-dirty"] : ""}`} />
                  {createDraftBreach && companyRisk && (
                    <span className={styles["cio-risk-alert"]}>
                      <RiskWarningIcon label="CIO" targetPct={createDraft.cio_target_pct!} risk={companyRisk} />
                    </span>
                  )}
                </span>
              </Tooltip>
          : <ValueWithMarkers
              markers={cioRiskBreach && order.cio_target_pct != null && companyRisk && <RiskWarningIcon label="CIO" targetPct={order.cio_target_pct} risk={companyRisk} />}
            >
              {isPending && order.cio_target_pct ? `${fmtNum(order.cio_target_pct)}%` : DASH}
            </ValueWithMarkers>}
        {isBulkEditing && cioRiskItem && (
          <Tooltip
            placement="bottom"
            title={
              <span>
                Maximum permissible position: {fmtNum(cioRiskItem.max_permissible_position, 1, 0)}%
                <br />
                <span style={{ opacity: 0.85 }}>
                  Notional ₹{formatCurrencyInCrores(cioRiskItem.notional, "INR", true)} Cr · Median ₹{formatCurrencyInCrores(cioRiskItem.median_tv_21d, "INR", true)} Cr
                </span>
              </span>
            }
          >
            <div className={styles["cio-max"]}>
              Max {fmtNum(cioRiskItem.max_permissible_position, 1, 0)}%
            </div>
          </Tooltip>
        )}
      </td>

      {/* Analyst IR — per-row, like the Model IR beside it: it is computed from the same
          horizon-scoped rec as the Analyst target, so spanning it would put a five-year
          implied return next to a three-year target on the Core row. */}
      <td className={`${styles.num} ${styles[getIrClass(analystIr)] ?? ""}`}>
        <Tooltip placement="top" title={buildIrTooltip("Analyst", analystRec, analystNameDisplay, analystTargetPrice, analystIr, first.latest_price, fmtRecPrice, fmtCmpTooltip, analystPrevOwner)}>
          <span>{analystIr != null ? `${analystIr > 0 ? "+" : ""}${fmtNum(analystIr)}%` : DASH}</span>
        </Tooltip>
      </td>

      {/* IC IR — per-row, for the same reason as the Analyst IR above. */}
      <td className={`${styles.num} ${styles[getIrClass(icIr)] ?? ""}`}>
        <Tooltip placement="top" title={buildIrTooltip("IC", icRec, icNameDisplay, icTargetPrice, icIr, first.latest_price, fmtRecPrice, fmtCmpTooltip, icPrevOwner)}>
          <span>{icIr != null ? `${icIr > 0 ? "+" : ""}${fmtNum(icIr)}%` : DASH}</span>
        </Tooltip>
      </td>

      {/* Model IR */}
      <td className={`${styles.num} ${styles[modelCellClass] ?? ""}`}>
        <Tooltip placement="top" title={
          <span>
            {buildModelIrTooltip(modelDecision, buyBelow, targetPrice, targetDate, order.latest_price, modelSuggestion, fmtRecPrice, fmtCmpTooltip, isGlobal)}
            {ivRec && (
              <>
                <span style={{ display: "block", borderTop: "1px solid rgba(255,255,255,0.25)", margin: "4px 0" }} />
                <span style={{ fontWeight: 600 }}>Intrinsic Value</span><br />
                {ivRec.decision_price != null && <>{`IV Price: ${fmtRecPrice(ivRec.decision_price)}`}<br /></>}
                {ivRec.target_price   != null && <>{`Target: ${fmtRecPrice(ivRec.target_price)}`}<br /></>}
                {ivRec.target_date    != null && <>{`Target Date: ${ivRec.target_date}`}<br /></>}
                {renderSuggestionLines(ivRec.suggestions, isGlobal)}
              </>
            )}
          </span>
        }>
          <span>
            {modelIr != null ? `${modelIr > 0 ? "+" : ""}${fmtNum(modelIr)}%` : DASH}
          </span>
        </Tooltip>
      </td>

      {/* Comment + actions */}
      <td>
        {isBulkEditing ? (
          <textarea
            key={isPending ? `comment-${order.id}` : `comment-${order.id}-${cp?.strategyGroup}`}
            defaultValue={isPending ? (pendingDraft.comment ?? order.comment ?? "") : (createDraft.comment ?? "")}
            disabled={createLocked}
            onBlur={e => isPending
              ? (props as PendingProps).handleBulkEditChange(order.id, "comment", e.target.value)
              : cp!.handleBulkCreateChange(order.company, cp!.strategyGroup, "comment", e.target.value)}
            placeholder="Comment"
            className={[
              styles["pot-inline-input"],
              styles["comment-input"],
              createLocked ? styles["input-locked"] : isDirty("comment") ? styles["field-dirty"] : "",
            ].filter(Boolean).join(" ")}
          />
        ) : isPending ? (
          <div className={styles["comment-cell"]}>
            <span className={styles["comment-text"]}>{order.comment || DASH}</span>
            <ScopeGate scope={WRITE_ORDERS_AND_RECOMMENDATIONS}>
              <span className={styles["row-actions"]}>
                <Tooltip title="Delete">
                  <Button
                    iconOnly
                    variant="danger"
                    className={styles["row-delete"]}
                    aria-label="Delete"
                    onClick={e => { e.stopPropagation(); (props as PendingProps).setConfirmDialog({ open: true, type: "delete", orderId: order.id, companyName: order.company_name }); }}
                  >
                    <Icon name="trash" size={14} />
                  </Button>
                </Tooltip>
              </span>
            </ScopeGate>
          </div>
        ) : null}
      </td>
    </tr>
  );
};

// ── Memoization ───────────────────────────────────────────────────────────
// Bulk-edit state lives at page level, so every draft change re-renders the
// whole list. Each row only depends on ITS OWN slice of bulkEditState /
// bulkCreateState — comparing just that slice lets the other N-1 rows skip
// their (expensive) re-render, which is what makes the Decision dropdown and
// other inline edits feel instant. Render output is unchanged.
const propsAreEqual = (prev: TableRowProps, next: TableRowProps): boolean => {
  if (
    prev.variant !== next.variant ||
    prev.order !== next.order ||
    prev.first !== next.first ||
    prev.isFirst !== next.isFirst ||
    prev.rowSpan !== next.rowSpan ||
    prev.isBulkEditing !== next.isBulkEditing ||
    (prev.isErrorRow ?? false) !== (next.isErrorRow ?? false) ||
    prev.lastOrderFilter !== next.lastOrderFilter ||
    prev.riskByCompanyId !== next.riskByCompanyId
  ) {
    return false;
  }

  if (prev.variant === "pending") {
    const p = prev as PendingProps;
    const n = next as PendingProps;
    return (
      p.bulkEditState[p.order.id] === n.bulkEditState[n.order.id] &&
      // A partial save advances the snapshot alone; the row must re-render to drop its dirty marks.
      p.bulkEditSnapshot[p.order.id] === n.bulkEditSnapshot[n.order.id] &&
      p.handleBulkEditChange === n.handleBulkEditChange &&
      p.setConfirmDialog === n.setConfirmDialog
    );
  }

  const p = prev as CreateProps;
  const n = next as CreateProps;
  if (p.strategyGroup !== n.strategyGroup) return false;
  const draftKey = `${p.order.company}_${p.strategyGroup}`;
  return (
    p.bulkCreateState[draftKey] === n.bulkCreateState[draftKey] &&
    p.bulkCreateSnapshot[draftKey] === n.bulkCreateSnapshot[draftKey] &&
    p.handleBulkCreateChange === n.handleBulkCreateChange
  );
};

export const TableRow = React.memo(TableRowComponent, propsAreEqual);
